import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import LegalFooter from "@/components/LegalFooter";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, ShieldCheck, X, Info } from "lucide-react";
import { api, login } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: "0€",
    period: "à vie",
    lookupKey: null,
    features: [
      "1 audit / 30 jours",
      "Score global & aperçu des problèmes",
      "5 recommandations clés",
      "Rapport standard",
    ],
    cta: "Commencer gratuitement",
    testid: "pricing-tier-free",
  },
  {
    id: "pro",
    name: "Pro",
    price: "19,90€",
    period: "/ mois",
    lookupKey: "revenuefix_pro_monthly",
    highlight: true,
    features: [
      "Jusqu'à 250 audits / 30 jours",
      "Rapport complet avec actions Shopify",
      "Recommandations IA détaillées",
      "Historique & suivi du score",
      "Export PDF",
    ],
    cta: "Commencer avec Pro",
    testid: "pricing-tier-pro",
  },
  {
    id: "agency",
    name: "Agency",
    price: "79€",
    period: "/ mois",
    lookupKey: "revenuefix_agency_monthly",
    features: [
      "Jusqu'à 1 000 audits / 30 jours",
      "Jusqu'à 15 boutiques",
      "Rapports avancés en marque blanche",
      "Historique multi-clients",
      "Accès API & webhooks",
      "Support prioritaire",
    ],
    cta: "Pour les agences",
    testid: "pricing-tier-agency",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);
  const [modalTier, setModalTier] = useState(null);
  const [b2bChecked, setB2bChecked] = useState(false);
  const [cgvVersion, setCgvVersion] = useState(null);

  useEffect(() => {
    api.get("/legal/version").then((r) => setCgvVersion(r.data.cgv_version)).catch(() => {});
  }, []);

  const openConsent = (tier) => {
    if (!user) {
      toast.info("Connectez-vous pour continuer");
      login();
      return;
    }
    if (!tier.lookupKey) {
      window.location.href = "/analyze";
      return;
    }
    setB2bChecked(false);
    setModalTier(tier);
  };

  const confirmCheckout = async () => {
    if (!modalTier) return;
    if (!b2bChecked) {
      toast.error("Veuillez confirmer votre qualité de professionnel et accepter les CGV.");
      return;
    }
    setLoadingKey(modalTier.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: modalTier.lookupKey,
        origin_url: window.location.origin,
        professional_confirmed: true,
        cgv_accepted: true,
        cgv_version: cgvVersion,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || "Impossible de démarrer le paiement.";
      toast.error(msg);
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#080A11] text-slate-100">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 pt-32 pb-24 relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[400px] glow-violet opacity-30" />

        <div className="relative text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-violet-400" />
            <span className="text-xs font-semibold uppercase tracking-widest text-violet-300">Pricing</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
            Choisissez votre plan
          </h1>
          <p className="mt-4 text-lg text-slate-400 max-w-2xl mx-auto">
            Simple, transparent, sans engagement. Annulez à tout moment.
          </p>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TIERS.map((tier) => (
            <div
              key={tier.id}
              data-testid={tier.testid}
              className={`relative rounded-3xl p-8 flex flex-col ${
                tier.highlight
                  ? "bg-gradient-to-br from-violet-600/20 via-[#0F1322] to-blue-600/10 border-2 border-violet-500/50 shadow-2xl shadow-violet-500/20 scale-105"
                  : "glass"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                  Le plus populaire
                </div>
              )}
              <div>
                <div className="font-display text-2xl font-bold text-white">{tier.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-extrabold text-white">{tier.price}</span>
                  <span className="text-sm text-slate-400">{tier.period}</span>
                </div>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-300">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      tier.highlight ? "bg-violet-500/30" : "bg-emerald-500/20"
                    }`}>
                      <Check className={`w-3 h-3 ${tier.highlight ? "text-violet-200" : "text-emerald-400"}`} />
                    </div>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`${tier.testid}-cta`}
                onClick={() => openConsent(tier)}
                disabled={loadingKey === tier.id}
                className={`mt-8 h-12 font-semibold ${
                  tier.highlight
                    ? "bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0 shadow-lg shadow-violet-600/25"
                    : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                }`}
              >
                {loadingKey === tier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : tier.cta}
              </Button>
            </div>
          ))}
        </div>

        <p className="flex items-center justify-center gap-2 text-center text-xs text-slate-500 mt-12">
          <ShieldCheck className="w-3.5 h-3.5" />
          Paiements sécurisés par Stripe
        </p>
      </div>

      {/* Pre-checkout consent modal */}
      {modalTier && (
        <div
          data-testid="checkout-consent-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="relative w-full max-w-xl rounded-2xl bg-[#0F1322] border border-slate-800 shadow-2xl p-6 sm:p-8">
            <button
              onClick={() => setModalTier(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
              data-testid="checkout-consent-close"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-violet-400" />
              <h2 className="font-display text-xl font-bold text-white">Confirmez votre abonnement {modalTier.name}</h2>
            </div>
            <div className="text-sm text-slate-300 space-y-3">
              <div>
                <span className="text-white font-semibold">Montant :</span> {modalTier.price} {modalTier.period}, TVA appliquée le cas échéant.
              </div>
              <ul className="list-disc list-inside space-y-1 text-slate-400 text-[13px] leading-relaxed">
                <li>RevenueFix AI est un service SaaS destiné exclusivement aux professionnels.</li>
                <li>Abonnement mensuel, prélevé automatiquement chaque mois par Stripe jusqu'à résiliation.</li>
                <li>L'accès au service payant démarre immédiatement après le paiement.</li>
                <li>Résiliable à tout moment depuis « Gérer mon abonnement » pour empêcher le prochain renouvellement.</li>
                <li>L'accès reste disponible jusqu'à la fin de la période mensuelle déjà payée.</li>
                <li>Voir la <Link to="/legal/cgv" className="underline text-violet-300">page CGV</Link> pour l'intégralité des conditions applicables.</li>
              </ul>
            </div>
            <div className="mt-6 space-y-3">
              <label className="flex items-start gap-3 text-sm text-slate-200 cursor-pointer" data-testid="checkout-consent-b2b-label">
                <input
                  type="checkbox"
                  data-testid="checkout-consent-b2b"
                  checked={b2bChecked}
                  onChange={(e) => setB2bChecked(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-violet-500"
                />
                <span>
                  Je confirme souscrire à RevenueFix AI en qualité de <strong className="text-white">professionnel</strong> et utiliser le service à des fins entrant dans le cadre de mon activité professionnelle. J'accepte les{" "}
                  <Link to="/legal/cgv" target="_blank" className="underline text-violet-300">Conditions Générales de Vente</Link>
                  {cgvVersion ? <span className="text-slate-500"> (version {cgvVersion})</span> : null}.
                </span>
              </label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => setModalTier(null)}
                data-testid="checkout-consent-cancel"
                className="text-slate-300 hover:text-white"
              >
                Annuler
              </Button>
              <Button
                data-testid="checkout-consent-confirm"
                onClick={confirmCheckout}
                disabled={!b2bChecked || loadingKey === modalTier.id}
                className="bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white border-0"
              >
                {loadingKey === modalTier.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continuer vers le paiement"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <LegalFooter />
    </div>
  );
}
